import dayjs from 'dayjs';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum DateRangePresetKey {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  TWODAYSAGO = 'twoDaysAgo',
  LAST3DAYS = 'last3days',
  LAST7DAYS = 'last7days',
  LAST30DAYS = 'last30days',
  THISMONTH = 'thismonth',
  LASTMONTH = 'lastmonth',
  LAST6MONTHS = 'last6months',
  NEXT6MONTHS = 'next6months',
  NEXT12MONTHS = 'next12months',
  THISYEAR = 'thisyear',
  LASTYEAR = 'lastyear',
  NEXTYEAR = 'nextyear',
  YTD = 'ytd',
  LAST12MONTHSROLLING = 'last12monthsRolling',
  LAST6MONTHSROLLING = 'last6monthsRolling',
  CUSTOM = 'custom',
}

export interface DateRangePreset {
  key: DateRangePresetKey;
  label: string;
  from: dayjs.Dayjs | null;
  to: dayjs.Dayjs | null;
}

export function useDefaultDateRangePresets({
  defaultPresetKey,
  onChange,
  visiblePresets,
  initialDateFrom,
  initialDateTo,
}: {
  defaultPresetKey: DateRangePresetKey;
  onChange?: (preset: DateRangePreset) => void;
  visiblePresets?: DateRangePresetKey[];
  initialDateFrom?: string;
  initialDateTo?: string;
}) {
  const { t } = useTranslation('global');
  const [activePreset, setActivePreset] = useState<DateRangePreset | null>(null);
  const [isRestoredFromUrl, setIsRestoredFromUrl] = useState(false);

  const allPresets = useMemo(() => {
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');
    return [
      {
        key: DateRangePresetKey.TODAY,
        label: t('datePicker.shortcuts.today'),
        from: today.startOf('day'),
        to: today.endOf('day'),
      },
      {
        key: DateRangePresetKey.YESTERDAY,
        label: t('datePicker.shortcuts.yesterday'),
        from: yesterday.startOf('day'),
        to: yesterday.endOf('day'),
      },
      {
        key: DateRangePresetKey.TWODAYSAGO,
        label: t('datePicker.shortcuts.twoDaysAgo'),
        from: today.subtract(2, 'day').startOf('day'),
        to: today.subtract(2, 'day').endOf('day'),
      },
      {
        key: DateRangePresetKey.LAST3DAYS,
        label: t('datePicker.shortcuts.last3days'),
        from: today.subtract(3, 'day').startOf('day'),
        to: today.subtract(1, 'day').endOf('day'),
      },
      {
        key: DateRangePresetKey.LAST7DAYS,
        label: t('datePicker.shortcuts.last7days'),
        from: today.subtract(7, 'day').startOf('day'),
        to: today.subtract(1, 'day').endOf('day'),
      },
      {
        key: DateRangePresetKey.LAST30DAYS,
        label: t('datePicker.shortcuts.last30days'),
        from: today.subtract(30, 'day').startOf('day'),
        to: today.subtract(1, 'day').endOf('day'),
      },
      {
        key: DateRangePresetKey.THISMONTH,
        label: t('datePicker.shortcuts.thisMonth'),
        from: today.startOf('month'),
        to: today.endOf('month'),
      },
      {
        key: DateRangePresetKey.LASTMONTH,
        label: t('datePicker.shortcuts.lastMonth'),
        from: today.subtract(1, 'month').startOf('month'),
        to: today.subtract(1, 'month').endOf('month'),
      },
      {
        key: DateRangePresetKey.LAST6MONTHS,
        label: t('datePicker.shortcuts.last6months'),
        from: today.subtract(6, 'month').startOf('month'),
        to: today.subtract(1, 'month').endOf('month'),
      },
      {
        key: DateRangePresetKey.NEXT6MONTHS,
        label: t('datePicker.shortcuts.next6months'),
        from: today.add(1, 'month').startOf('month'),
        to: today.add(6, 'month').endOf('month'),
      },
      {
        key: DateRangePresetKey.NEXT12MONTHS,
        label: t('datePicker.shortcuts.next12months'),
        from: today.add(1, 'month').startOf('month'),
        to: today.add(12, 'month').endOf('month'),
      },
      {
        key: DateRangePresetKey.THISYEAR,
        label: t('datePicker.shortcuts.thisYear'),
        from: today.startOf('year'),
        to: today.endOf('year'),
      },
      {
        key: DateRangePresetKey.LASTYEAR,
        label: t('datePicker.shortcuts.lastYear'),
        from: today.subtract(1, 'year').startOf('year'),
        to: today.subtract(1, 'year').endOf('year'),
      },
      {
        key: DateRangePresetKey.NEXTYEAR,
        label: t('datePicker.shortcuts.nextYear'),
        from: today.add(1, 'year').startOf('year'),
        to: today.add(1, 'year').endOf('year'),
      },
      {
        key: DateRangePresetKey.YTD,
        label: t('datePicker.shortcuts.ytd'),
        from: today.startOf('year'),
        to: today.endOf('day'),
      },
      {
        key: DateRangePresetKey.LAST12MONTHSROLLING,
        label: t('datePicker.shortcuts.last12months'),
        from: today.subtract(12, 'month').startOf('day'),
        to: today.endOf('day'),
      },
      {
        key: DateRangePresetKey.LAST6MONTHSROLLING,
        label: t('datePicker.shortcuts.last6months'),
        from: today.subtract(6, 'month').startOf('day'),
        to: today.endOf('day'),
      },
      {
        key: DateRangePresetKey.CUSTOM,
        label: t('datePicker.shortcuts.custom'),
        from: null,
        to: null,
      },
    ];
  }, []);

  // Filter presets based on visiblePresets prop
  const presets = useMemo(() => {
    if (!visiblePresets || visiblePresets.length === 0) {
      return allPresets;
    }
    return allPresets.filter((preset) => visiblePresets.includes(preset.key));
  }, [allPresets, visiblePresets]);

  // Set initial preset on mount - check URL dates first, then fall back to default
  useEffect(() => {
    if (presets.length > 0 && !activePreset) {
      // If we have dates from URL, try to find a matching preset
      if (initialDateFrom && initialDateTo) {
        const urlFrom = dayjs(initialDateFrom);
        const urlTo = dayjs(initialDateTo);

        // Try to find a preset that matches the URL dates
        const matchingPreset = presets.find((preset) => {
          if (!preset.from || !preset.to || preset.key === DateRangePresetKey.CUSTOM) return false;
          return (
            preset.from.format('YYYY-MM-DD') === urlFrom.format('YYYY-MM-DD') &&
            preset.to.format('YYYY-MM-DD') === urlTo.format('YYYY-MM-DD')
          );
        });

        if (matchingPreset) {
          setActivePreset(matchingPreset);
          setIsRestoredFromUrl(true);
          return;
        }

        // No matching preset found, use CUSTOM with the URL dates
        const customPreset = presets.find((p) => p.key === DateRangePresetKey.CUSTOM);
        if (customPreset) {
          setActivePreset({ ...customPreset, from: urlFrom, to: urlTo });
          setIsRestoredFromUrl(true);
          return;
        }
      }

      // No URL dates, use default preset
      if (defaultPresetKey) {
        const defaultPreset = presets.find((preset) => preset.key === defaultPresetKey);
        if (defaultPreset) {
          setActivePreset(defaultPreset);
          // Immediately call onChange to ensure dates are set
          if (defaultPreset.from && defaultPreset.to && onChange) {
            onChange(defaultPreset);
          }
        }
      }
    }
  }, [presets, defaultPresetKey, initialDateFrom, initialDateTo]);

  // Trigger onChange when activePreset changes (user clicks a different preset)
  // Skip if restored from URL since dates are already set in context
  useEffect(() => {
    if (isRestoredFromUrl) {
      setIsRestoredFromUrl(false);
      return;
    }
    if (activePreset && activePreset.from && activePreset.to && onChange) {
      onChange(activePreset);
    }
  }, [activePreset]);

  return { presets, activePreset, setActivePreset };
}
